import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtDate, fmtDateTime } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, Activity, Trash2, RefreshCw,
  Calendar, FileText, AlertTriangle, ShieldAlert, Building2,
  User, Clock, DollarSign, ChevronDown, ChevronUp, Info,
  Phone, Mail, MapPin, Briefcase, BadgeCheck, X,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

// ─── Helper sub-components ────────────────────────────────────────────────────

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2.5 label-eyebrow font-semibold text-left ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);

const OPEN_DATA = {
  dobNowPermits: "https://data.cityofnewyork.us/resource/rbx6-tga4.json",
  dobNowFilings: "https://data.cityofnewyork.us/resource/w9ak-ipjd.json",
  dobViolations: "https://data.cityofnewyork.us/resource/3h2n-5cm9.json",
  ecbViolations: "https://data.cityofnewyork.us/resource/6bgk-3dad.json",
  oathCases: "https://data.cityofnewyork.us/resource/jz4z-kudi.json",
  serviceRequests311: "https://data.cityofnewyork.us/resource/erm2-nwe9.json",
  hpdViolations: "https://data.cityofnewyork.us/resource/wvxf-dwi5.json",
};

const errorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || fallback;
};

function openDataUrl(base, params = {}) {
  const qs = new URLSearchParams({ "$limit": "20" });
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") qs.set(key, String(value).trim());
  });
  return `${base}?${qs.toString()}`;
}

function permitDobUrl(p) {
  if (p.filing_number) return openDataUrl(OPEN_DATA.dobNowPermits, { job_filing_number: p.filing_number });
  if (p.permit_number) return openDataUrl(OPEN_DATA.dobNowPermits, { work_permit: p.permit_number });
  if (p.job_number) return openDataUrl(OPEN_DATA.dobNowPermits, { job_filing_number: p.job_number });
  return openDataUrl(OPEN_DATA.dobNowPermits, { bin: p.bin });
}

function filingDobUrl(f) {
  return openDataUrl(OPEN_DATA.dobNowFilings, { job_filing_number: f.filing_number || f.job_number });
}

function violationDobUrl(v) {
  if (v.source === "ecb") {
    return openDataUrl(OPEN_DATA.ecbViolations, {
      ecb_violation_number: v.ecb_number || v.violation_number,
      bin: v.bin,
    });
  }
  if (v.source === "oath") {
    return openDataUrl(OPEN_DATA.oathCases, {
      ticket_number: v.violation_number || v.ecb_number,
    });
  }
  if (v.source === "311") {
    return openDataUrl(OPEN_DATA.serviceRequests311, {
      unique_key: v.service_request_number || v.violation_number,
    });
  }
  if (v.source === "hpd") {
    return openDataUrl(OPEN_DATA.hpdViolations, {
      violationid: v.hpd_violation_id || v.violation_number,
      bbl: v.bbl,
    });
  }
  return openDataUrl(OPEN_DATA.dobViolations, {
    violation_number: v.violation_number,
    bin: v.bin,
  });
}

function violationSourceLabel(v) {
  if (v.source_detail === "fdny_oath") return "FDNY";
  if (v.source === "oath") return "OATH";
  if (v.source === "311") return v.source_detail === "hpd_311" ? "HPD 311" : "311";
  if (v.source === "hpd") return "HPD";
  if (v.source === "ecb") return "ECB";
  return "DOB";
}

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function matchesDateRule(value, rule, target) {
  if (!target || rule === "all") return true;
  const d = toDate(value);
  if (!d) return false;
  const boundary = new Date(`${target}${rule === "before" ? "T23:59:59" : "T00:00:00"}`);
  return rule === "before" ? d <= boundary : d >= boundary;
}

function uniqueOptions(rows, getter) {
  return [...new Set(rows.map(getter).filter(Boolean).map(String))].sort();
}

const PAGE_SIZE = 10;

function pageRows(rows, page, size = PAGE_SIZE) {
  const safePage = Math.max(1, page || 1);
  return rows.slice((safePage - 1) * size, safePage * size);
}

function pageCount(rows, size = PAGE_SIZE) {
  return Math.max(1, Math.ceil((rows?.length || 0) / size));
}

function filingDateValue(f, field) {
  if (field === "approved_date") return f.approved_date || f.plan_approval_date;
  if (field === "filing_date") return f.filing_date;
  return f.current_status_date || f.updated_at || f.filing_date || f.approved_date || f.plan_approval_date;
}

function MetaChip({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs text-zinc-600">
      <Icon size={11} className="text-zinc-400 shrink-0" />
      <span className="text-zinc-500">{label}:</span>
      <span className="font-medium text-zinc-800 truncate max-w-[160px]" title={value}>{value}</span>
    </div>
  );
}

// Permit list row with full-detail drill-in
function PermitRow({ p, onRefresh, refreshing, onOpenDetails }) {
  const [open, setOpen] = useState(false);
  const hasExtra = p.issuance_date || p.expiration_date || p.owner || p.applicant || p.contractor || p.bin || p.estimated_cost;

  return (
    <>
      <tr
        className="row-hover border-b border-zinc-100 cursor-pointer"
        data-testid={`permit-row-${p.permit_id}`}
        onClick={() => onOpenDetails(p)}
      >
        <Td className="font-mono text-xs">{p.permit_number}</Td>
        <Td>{p.permit_type}</Td>
        <Td><StatusBadge status={p.status} testId={`permit-status-${p.permit_id}`} /></Td>
        <Td>{p.issuance_date ? fmtDate(p.issuance_date) : <span className="text-zinc-400">—</span>}</Td>
        <Td>
          {p.expiration_date ? (
            <span className={isExpiringSoon(p.expiration_date) ? "text-amber-600 font-semibold" : ""}>
              {fmtDate(p.expiration_date)}
            </span>
          ) : "—"}
        </Td>
        <Td>
          {p.days_in_status}d
          {p.flagged && <span className="status-pill status-warning ml-1">Delayed</span>}
        </Td>
        <Td className="text-right">
          <div className="flex items-center justify-end gap-1.5">
            {hasExtra && (
              <button
                type="button"
                onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
                className="inline-flex items-center gap-0.5 text-xs px-1.5 py-1 text-zinc-400 hover:text-zinc-700"
                aria-label={open ? "Hide quick permit details" : "Show quick permit details"}
              >
                {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
            )}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onOpenDetails(p); }}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50"
            >
              <Info size={12} /> Details
            </button>
            <button
              onClick={e => { e.stopPropagation(); onRefresh(); }}
              disabled={refreshing}
              data-testid={`refresh-permit-${p.permit_id}`}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50"
            >
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <a
              href={permitDobUrl(p)}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50"
              onClick={e => e.stopPropagation()}
            >
              <ExternalLink size={12} /> DOB
            </a>
          </div>
        </Td>
      </tr>
      {open && hasExtra && (
        <tr className="bg-zinc-50 border-b border-zinc-100">
          <td colSpan={7} className="px-4 pb-3 pt-1">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <MetaChip icon={Building2} label="BIN" value={p.bin} />
              <MetaChip icon={User} label="Owner" value={p.owner} />
              <MetaChip icon={User} label="Applicant" value={p.applicant} />
              <MetaChip icon={User} label="Contractor" value={p.contractor} />
              <MetaChip icon={Calendar} label="Issued" value={fmtDate(p.issuance_date)} />
              <MetaChip icon={Calendar} label="Expires" value={fmtDate(p.expiration_date)} />
              <MetaChip icon={DollarSign} label="Est. Cost" value={p.estimated_cost ? `$${Number(p.estimated_cost).toLocaleString()}` : null} />
              <MetaChip icon={Info} label="Community Board" value={p.community_board} />
              <MetaChip icon={Info} label="Block/Lot" value={p.block && p.lot ? `${p.block}/${p.lot}` : null} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function isExpiringSoon(dateStr) {
  if (!dateStr) return false;
  try {
    const d = new Date(dateStr);
    const days = (d - Date.now()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 30;
  } catch { return false; }
}

// ─── Filing status timeline ───────────────────────────────────────────────────

const FILING_STAGES = [
  { key: "Pre-Filed",        label: "Pre-Filed" },
  { key: "Filed",            label: "Filed" },
  { key: "Payment Required", label: "Payment" },
  { key: "Plan Review",      label: "Plan Review" },
  { key: "Plan Approved",    label: "Plan Approved" },
  { key: "Permit Issued",    label: "Permit Issued" },
];
const STAGE_DANGER = new Set(["Plan Disapproved"]);

function FilingTimeline({ status }) {
  const idx = FILING_STAGES.findIndex(s => s.key === status);
  const isDanger = STAGE_DANGER.has(status);
  return (
    <div className="flex items-center gap-0 overflow-x-auto py-1">
      {FILING_STAGES.map((stage, i) => {
        const done = idx >= 0 && i <= idx && !isDanger;
        const current = i === idx && !isDanger;
        return (
          <React.Fragment key={stage.key}>
            <div className="flex flex-col items-center shrink-0">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2
                ${done ? "bg-zinc-900 border-zinc-900 text-white" :
                  "bg-white border-zinc-300 text-zinc-400"}`}>
                {done ? (current ? "●" : "✓") : "○"}
              </div>
              <span className={`text-[9px] mt-1 text-center w-14 leading-tight
                ${current ? "font-bold text-zinc-900" : "text-zinc-500"}`}>
                {stage.label}
              </span>
            </div>
            {i < FILING_STAGES.length - 1 && (
              <div className={`h-0.5 flex-1 min-w-[16px] mx-0.5 mb-4 ${i < idx && !isDanger ? "bg-zinc-900" : "bg-zinc-200"}`} />
            )}
          </React.Fragment>
        );
      })}
      {isDanger && (
        <div className="flex items-center gap-1 ml-3 shrink-0">
          <AlertTriangle size={14} className="text-red-500" />
          <span className="text-xs font-semibold text-red-600">{status}</span>
        </div>
      )}
    </div>
  );
}

// ─── Violations panel ─────────────────────────────────────────────────────────

function FilingStatusSummary({ filing, compact = false }) {
  return (
    <div className={`${compact ? "px-4 py-3 bg-zinc-50/50" : ""}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-zinc-500">Current DOB status</span>
        <StatusBadge status={filing.filing_status || "Unknown"} />
        {filing.raw_filing_status && filing.raw_filing_status !== filing.filing_status && (
          <span className="text-xs text-zinc-500">Raw: {filing.raw_filing_status}</span>
        )}
      </div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2">
        <MetaChip icon={Calendar} label="Current status date" value={fmtDate(filing.current_status_date || filing.updated_at)} />
        <MetaChip icon={Calendar} label="Filed" value={fmtDate(filing.filing_date)} />
        <MetaChip icon={Calendar} label="Approved" value={fmtDate(filing.approved_date || filing.plan_approval_date)} />
        <MetaChip icon={Calendar} label="First permit" value={fmtDate(filing.first_permit_date)} />
      </div>
      {!compact && (
        <p className="text-xs text-zinc-500 mt-2">
          Based on DOB NOW Open Data's current filing record. Full status history and payment history may not be available.
        </p>
      )}
    </div>
  );
}

function ViolationCard({ v, onOpenDetails }) {
  const [open, setOpen] = useState(false);
  const isSWO = v.is_stop_work_order;
  const isECB = v.source === "ecb";
  const isOATH = v.source === "oath";
  const is311 = v.source === "311";
  const isHPD = v.source === "hpd";
  const isFDNY = v.source_detail === "fdny_oath";
  const isPast = v.is_past_violation;
  const sourceLabel = violationSourceLabel(v);

  return (
    <div className={`border rounded-sm overflow-hidden ${isSWO ? "border-red-300 bg-red-50" : isECB || isOATH ? "border-amber-200 bg-amber-50" : isHPD ? "border-violet-200 bg-violet-50" : is311 ? "border-sky-200 bg-sky-50" : isPast ? "border-zinc-200 bg-zinc-50 opacity-80" : "border-zinc-200 bg-white"}`}>
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer"
        onClick={() => onOpenDetails(v)}
      >
        <div className="mt-0.5 shrink-0">
          {isSWO
            ? <ShieldAlert size={16} className="text-red-600" />
            : <AlertTriangle size={16} className={isECB || isOATH ? "text-amber-600" : isHPD ? "text-violet-600" : is311 ? "text-sky-600" : isPast ? "text-zinc-400" : "text-orange-500"} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isSWO && <span className="status-pill status-danger">Stop Work Order</span>}
            {isECB && !isSWO && <span className="status-pill status-warning">ECB Violation</span>}
            {isOATH && !isSWO && <span className="status-pill status-warning">{isFDNY ? "FDNY OATH" : "OATH"}</span>}
            {isHPD && !isSWO && <span className="status-pill status-warning">HPD Formal Violation</span>}
            {is311 && !isSWO && <span className="status-pill status-neutral">{v.category || "311 Complaint"}</span>}
            {!isECB && !isOATH && !isHPD && !is311 && !isSWO && <span className="status-pill status-neutral">{v.category || "DOB Violation"}</span>}
            {isPast && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 border border-zinc-300 rounded-sm px-1.5 py-0.5">
                Pre-existing · No alert sent
              </span>
            )}
            <span className="font-mono text-xs text-zinc-600">
              #{v.violation_number || v.ecb_number}
            </span>
            <span className="text-xs text-zinc-500">{fmtDate(v.issue_date)}</span>
          </div>
          <p className="text-sm mt-1 text-zinc-800 line-clamp-2">{v.description || v.description}</p>
          {(isECB || isOATH) && v.balance_due && Number(v.balance_due) > 0 && (
            <p className="text-xs text-red-600 font-semibold mt-1">Balance due: ${Number(v.balance_due).toLocaleString()}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={violationDobUrl(v)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-200 rounded-sm bg-white/80 hover:bg-white text-zinc-700"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink size={12} /> {sourceLabel}
          </a>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
            className="text-zinc-400 hover:text-zinc-700"
            aria-label={open ? "Hide quick violation details" : "Show quick violation details"}
          >
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-3 border-t border-zinc-100 bg-white/60 text-xs text-zinc-600 space-y-1 pt-2">
          {v.violation_type && <div><span className="text-zinc-400">Type:</span> {v.violation_type}</div>}
          {v.infraction_code && <div><span className="text-zinc-400">Infraction Code:</span> {v.infraction_code}</div>}
          {v.source && <div><span className="text-zinc-400">Source:</span> {sourceLabel}</div>}
          {v.hearing_status && <div><span className="text-zinc-400">Hearing:</span> {v.hearing_status}</div>}
          {v.hearing_result && <div><span className="text-zinc-400">Result:</span> {v.hearing_result}</div>}
          {v.compliance_status && <div><span className="text-zinc-400">Compliance:</span> {v.compliance_status}</div>}
          {v.disposition && <div><span className="text-zinc-400">Disposition:</span> {v.disposition}</div>}
          {v.disposition_date && <div><span className="text-zinc-400">Resolved:</span> {fmtDate(v.disposition_date)}</div>}
          {v.severity && <div><span className="text-zinc-400">Severity:</span> {v.severity}</div>}
          {v.current_status && <div><span className="text-zinc-400">Current status:</span> {v.current_status}</div>}
          {v.original_correct_by_date && <div><span className="text-zinc-400">Correct by:</span> {fmtDate(v.original_correct_by_date)}</div>}
          {v.certified_date && <div><span className="text-zinc-400">Certified:</span> {fmtDate(v.certified_date)}</div>}
          {(isECB || isOATH) && v.penalty && <div><span className="text-zinc-400">Penalty:</span> ${Number(v.penalty).toLocaleString()}</div>}
          {(isECB || isOATH) && v.amount_paid && <div><span className="text-zinc-400">Amount paid:</span> ${Number(v.amount_paid).toLocaleString()}</div>}
          {v.resolution_description && <div><span className="text-zinc-400">Resolution:</span> {v.resolution_description}</div>}
          {v.respondent && <div><span className="text-zinc-400">Respondent:</span> {v.respondent}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

function FilterPanel({ children }) {
  return (
    <div className="border border-zinc-200 bg-zinc-50 rounded-sm p-3 mb-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {children}
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1">{label}</span>
      {children}
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, children }) {
  return (
    <FilterField label={label}>
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full px-2.5 py-2 border border-zinc-200 rounded-sm bg-white text-sm">
        {children || <option value="all">All</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </FilterField>
  );
}

function FilterDate({ label, value, onChange }) {
  return (
    <FilterField label={label}>
      <input type="date" value={value} onChange={e => onChange(e.target.value)} className="w-full px-2.5 py-2 border border-zinc-200 rounded-sm bg-white text-sm" />
    </FilterField>
  );
}

function FilterCheck({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-zinc-700 pt-6">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="accent-zinc-950" />
      {label}
    </label>
  );
}

const DEFAULT_FILTERS = {
  permitDateField: "issuance_date",
  permitDateRule: "all",
  permitDate: "",
  permitStatus: "all",
  permitType: "all",
  expiringOnly: false,
  flaggedOnly: false,
  filingDateField: "current_status_date",
  filingDateRule: "all",
  filingDate: "",
  filingStatus: "all",
  filingType: "all",
  violationDateRule: "all",
  violationDate: "",
  violationState: "active",
  violationSource: "all",
  violationType: "all",
  stopWorkOnly: false,
  balanceDueOnly: false,
  showPreExisting: false,
};

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("permits");
  const [refreshing, setRefreshing] = useState(false);
  const [contactIntel, setContactIntel] = useState(null);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [propertyIntel, setPropertyIntel] = useState(null);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [pages, setPages] = useState({
    permits: 1,
    filings: 1,
    violations: 1,
    contacts: 1,
    inspections: 1,
    activity: 1,
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const load = useCallback(async () => {
    const r = await api.get(`/jobs/${id}`);
    setData(r.data);
  }, [id]);

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const r = await api.get(`/jobs/${id}/contacts`);
      setContactIntel(r.data);
    } catch (e) {
      toast.error(errorMessage(e, "Contact data could not be loaded"));
    } finally {
      setContactsLoading(false);
    }
  }, [id]);

  const loadProperty = useCallback(async () => {
    setPropertyLoading(true);
    try {
      const r = await api.get(`/jobs/${id}/property`);
      setPropertyIntel(r.data);
    } catch (e) {
      toast.error(errorMessage(e, "Property data could not be loaded"));
    } finally {
      setPropertyLoading(false);
    }
  }, [id]);

  useEffect(() => { load().catch(() => navigate("/dashboard")); }, [id, load, navigate]);

  useEffect(() => {
    setContactIntel(null);
    setPropertyIntel(null);
    setPages({
      permits: 1,
      filings: 1,
      violations: 1,
      contacts: 1,
      inspections: 1,
      activity: 1,
    });
  }, [id]);

  useEffect(() => {
    if (tab === "contacts" && !contactIntel && !contactsLoading) loadContacts();
  }, [tab, contactIntel, contactsLoading, loadContacts]);

  useEffect(() => {
    if (tab === "property" && !propertyIntel && !propertyLoading) loadProperty();
  }, [tab, propertyIntel, propertyLoading, loadProperty]);

  useEffect(() => {
    const status = data?.job?.import_status;
    if (!["queued", "running"].includes(status)) return undefined;
    const timer = setInterval(() => {
      load().catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [data?.job?.import_status, load]);

  const simulate = async (pid) => {
    try {
      const r = await api.post(`/permits/${pid}/simulate`);
      toast.success(`Status changed: ${r.data.old_status} → ${r.data.new_status}`);
      load();
    } catch { toast.error("Simulation failed"); }
  };

  const setPage = (key, page) => setPages(prev => ({ ...prev, [key]: Math.max(1, page) }));
  const resetPage = (key = tab) => setPages(prev => ({ ...prev, [key]: 1 }));
  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    resetPage(tab);
  };
  const resetFilters = (...keys) => setFilters(prev => {
    const next = { ...prev };
    keys.forEach(key => { next[key] = DEFAULT_FILTERS[key]; });
    resetPage(tab);
    return next;
  });
  const openRecordDetails = (type, record) => setDetailRecord({ type, record });

  const refresh = async () => {
    setRefreshing(true);
    const t = toast.loading("Checking NYC Open Data — permits, filings & violations…");
    try {
      const r = await api.post(`/jobs/${id}/refresh-full`);
      toast.success(
        `Refreshed. ${r.data.permit_changes} permit change${r.data.permit_changes === 1 ? "" : "s"} detected.`,
        { id: t }
      );
      load();
    } catch (e) {
      const fullRefreshMessage = errorMessage(e, "");
      if (fullRefreshMessage) {
        toast.error(fullRefreshMessage, { id: t });
      }
      // Fall back to basic permit refresh
      try {
        const r2 = await api.post("/permits/refresh");
        toast.success(`Refreshed. ${r2.data.changes} status change${r2.data.changes === 1 ? "" : "s"} detected.`, { id: t });
        load();
      } catch (fallbackErr) { toast.error(errorMessage(fallbackErr, fullRefreshMessage || "Refresh failed"), { id: t }); }
    } finally { setRefreshing(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this job and all its permits, filings, and violations?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Job deleted");
    navigate("/dashboard");
  };

  if (!data) return <LoadingSkeleton />;

  const { job, permits, inspections, activity, filings = [], violations = [] } = data;
  const permitStatuses = uniqueOptions(permits, p => p.status);
  const permitTypes = uniqueOptions(permits, p => p.permit_type);
  const filingStatuses = uniqueOptions(filings, f => f.filing_status);
  const filingTypes = uniqueOptions(filings, f => f.filing_type);
  const violationTypes = uniqueOptions(violations, v => v.violation_type || v.category || v.ecb_status);
  const activeViolationCount = violations.filter(v => (v.violation_state || (v.is_relevant ? "active" : "history")) === "active").length;
  const historyViolationCount = Math.max(0, violations.length - activeViolationCount);

  const filteredPermits = permits.filter(p => {
    if (filters.permitStatus !== "all" && p.status !== filters.permitStatus) return false;
    if (filters.permitType !== "all" && p.permit_type !== filters.permitType) return false;
    if (filters.expiringOnly && !isExpiringSoon(p.expiration_date)) return false;
    if (filters.flaggedOnly && !p.flagged) return false;
    if (!matchesDateRule(p[filters.permitDateField], filters.permitDateRule, filters.permitDate)) return false;
    return true;
  });

  const filteredFilings = filings.filter(f => {
    if (filters.filingStatus !== "all" && f.filing_status !== filters.filingStatus) return false;
    if (filters.filingType !== "all" && f.filing_type !== filters.filingType) return false;
    if (!matchesDateRule(filingDateValue(f, filters.filingDateField), filters.filingDateRule, filters.filingDate)) return false;
    return true;
  });

  const filteredViolations = violations.filter(v => {
    const state = v.violation_state || (v.is_relevant ? "active" : "history");
    if (filters.violationState !== "all" && state !== filters.violationState) return false;
    if (!filters.showPreExisting && v.is_past_violation) return false;
    if (filters.violationSource !== "all" && v.source !== filters.violationSource) return false;
    const type = v.violation_type || v.category || v.ecb_status;
    if (filters.violationType !== "all" && type !== filters.violationType) return false;
    if (filters.stopWorkOnly && !v.is_stop_work_order) return false;
    if (filters.balanceDueOnly && !(Number(v.balance_due || 0) > 0)) return false;
    if (!matchesDateRule(v.issue_date, filters.violationDateRule, filters.violationDate)) return false;
    return true;
  });

  const pagedPermits = pageRows(filteredPermits, pages.permits);
  const pagedFilings = pageRows(filteredFilings, pages.filings);
  const pagedViolations = pageRows(filteredViolations, pages.violations);
  const contactRows = contactIntel?.contacts || [];
  const pagedContacts = pageRows(contactRows, pages.contacts);
  const pagedInspections = pageRows(inspections, pages.inspections);
  const pagedActivity = pageRows(activity, pages.activity);

  const dob_violations = pagedViolations.filter(v => v.source === "dob");
  const ecb_violations = pagedViolations.filter(v => v.source === "ecb");
  const oath_violations = pagedViolations.filter(v => v.source === "oath" && v.source_detail !== "fdny_oath");
  const fdny_violations = pagedViolations.filter(v => v.source_detail === "fdny_oath");
  const hpd_violations = pagedViolations.filter(v => v.source === "hpd");
  const service_requests_311 = pagedViolations.filter(v => v.source === "311");
  const visibleStopWorkOrder = filteredViolations.some(v => v.is_stop_work_order);

  // Surface the latest active filing without implying DOB provides a full milestone history.
  const activeFilings = filings.filter(f => !["Withdrawn", "Superseded"].includes(f.filing_status));
  const latestFiling = activeFilings[0] || filings[0];

  const tabDefs = [
    { k: "permits",    label: `Permits (${filteredPermits.length})` },
    { k: "filings",    label: `Filings (${filteredFilings.length})`, badge: filteredFilings.some(f => f.filing_status === "Plan Disapproved") ? "!" : null },
    { k: "violations", label: `Violations (${activeViolationCount})`, badge: activeViolationCount > 0 ? activeViolationCount : null, danger: visibleStopWorkOrder },
    { k: "property",   label: "Property" },
    { k: "contacts",   label: contactIntel ? `Contacts (${contactIntel.contacts?.length || 0})` : "Contacts" },
    { k: "inspections", label: `Inspections (${inspections.length})` },
    { k: "activity",   label: "Activity" },
  ];

  return (
    <div className="px-4 sm:px-8 py-8 max-w-7xl mx-auto" data-testid="job-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-3">
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <span className="label-eyebrow">{job.borough} • {job.job_type}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2" data-testid="job-name">
            {job.nickname}
          </h1>
          <p className="text-sm text-zinc-600 mt-1">{job.address}{job.zip ? `, NY ${job.zip}` : ""}</p>
          <p className="text-xs text-zinc-500 mt-1">Added {fmtDate(job.created_at)}</p>
        </div>
        <button onClick={remove} data-testid="delete-job-button"
          className="self-start md:self-auto inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-sm text-sm hover:bg-red-50 hover:border-red-200 hover:text-red-700">
          <Trash2 size={14} /> Delete job
        </button>
      </div>

      {["queued", "running"].includes(job.import_status) && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="job-import-banner">
          <RefreshCw size={16} className="text-amber-700 animate-spin shrink-0" />
          <div>
            <p className="text-sm font-semibold text-zinc-900">Loading permit data</p>
            <p className="text-xs text-zinc-600 mt-0.5">Permits, filings, and violations are being imported in the background. This page will update automatically.</p>
          </div>
        </div>
      )}

      {job.import_status === "failed" && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-sm" data-testid="job-import-failed">
          <AlertTriangle size={16} className="text-red-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700">Some permit data did not finish loading</p>
            <p className="text-xs text-red-600 mt-0.5">Use Refresh to try again.</p>
          </div>
        </div>
      )}

      {/* Stop Work Order banner */}
      {visibleStopWorkOrder && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-300 rounded-sm">
          <ShieldAlert size={18} className="text-red-600 shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-700">Stop-Work Order in Effect</p>
            <p className="text-xs text-red-600 mt-0.5">Work at this site has been ordered to stop by the DOB. See the Violations tab for details.</p>
          </div>
        </div>
      )}

      {/* Current DOB filing status */}
      {latestFiling && (
        <div className="mb-4 bg-white border border-zinc-200 rounded-sm px-4 py-3">
          <p className="text-xs text-zinc-500 mb-2 font-semibold uppercase tracking-wide">
            Current DOB Filing Status - {latestFiling.filing_number}
          </p>
          <FilingStatusSummary filing={latestFiling} />
          {latestFiling.plan_examiner && (
            <p className="text-xs text-zinc-500 mt-2">
              <span className="text-zinc-400">Examiner:</span> {latestFiling.plan_examiner}
            </p>
          )}
          {latestFiling.filing_status === "Plan Disapproved" && latestFiling.disapproval_reason && (
            <p className="text-xs text-red-600 mt-1 font-medium">
              Reason: {latestFiling.disapproval_reason}
            </p>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={refresh} disabled={refreshing} data-testid="refresh-permits-button"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 text-zinc-700 disabled:opacity-50">
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          Refresh — permits, filings &amp; violations
        </button>
        <span className="text-xs text-zinc-500">Auto-checked 4× daily</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200 mb-4 overflow-x-auto" data-testid="job-tabs">
        {tabDefs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2 text-sm font-semibold tracking-tight whitespace-nowrap flex items-center gap-1.5
              ${tab === t.k ? "border-b-2 border-zinc-950 text-zinc-950" : "text-zinc-500 hover:text-zinc-800"}`}>
            {t.label}
            {t.badge && (
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold
                ${t.danger ? "bg-red-500 text-white" : "bg-amber-400 text-zinc-900"}`}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Permits tab ── */}
      {tab === "permits" && (
        <>
        <FilterPanel>
          <FilterSelect label="Status" value={filters.permitStatus} onChange={v => setFilter("permitStatus", v)} options={permitStatuses} />
          <FilterSelect label="Type" value={filters.permitType} onChange={v => setFilter("permitType", v)} options={permitTypes} />
          <FilterSelect label="Date field" value={filters.permitDateField} onChange={v => setFilter("permitDateField", v)} options={[]}>
            <option value="issuance_date">Issued</option>
            <option value="expiration_date">Expires</option>
            <option value="filing_date">Filed</option>
          </FilterSelect>
          <FilterSelect label="Date rule" value={filters.permitDateRule} onChange={v => setFilter("permitDateRule", v)} options={[]}>
            <option value="all">Any date</option>
            <option value="after">After date</option>
            <option value="before">Before date</option>
          </FilterSelect>
          <FilterDate label="Date" value={filters.permitDate} onChange={v => setFilter("permitDate", v)} />
          <FilterCheck label="Expiring in 30 days" checked={filters.expiringOnly} onChange={v => setFilter("expiringOnly", v)} />
          <FilterCheck label="Delayed only" checked={filters.flaggedOnly} onChange={v => setFilter("flaggedOnly", v)} />
          <button type="button" onClick={() => resetFilters("permitDateField", "permitDateRule", "permitDate", "permitStatus", "permitType", "expiringOnly", "flaggedOnly")} className="self-end px-3 py-2 border border-zinc-200 rounded-sm bg-white text-sm font-semibold hover:bg-zinc-50">
            Reset permits
          </button>
        </FilterPanel>
        <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>Permit #</Th><Th>Type</Th><Th>Status</Th>
                <Th>Issued</Th><Th>Expires</Th><Th>Days in status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredPermits.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">No permits match these filters.</td></tr>
              )}
              {pagedPermits.map(p => (
                <PermitRow key={p.permit_id} p={p} onRefresh={refresh} refreshing={refreshing} onOpenDetails={record => openRecordDetails("permit", record)} />
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls page={pages.permits} total={filteredPermits.length} onPage={page => setPage("permits", page)} />
        </>
      )}

      {/* ── Filings tab ── */}
      {tab === "filings" && (
        <div className="space-y-3">
          <FilterPanel>
            <FilterSelect label="Status" value={filters.filingStatus} onChange={v => setFilter("filingStatus", v)} options={filingStatuses} />
            <FilterSelect label="Type" value={filters.filingType} onChange={v => setFilter("filingType", v)} options={filingTypes} />
            <FilterSelect label="Date field" value={filters.filingDateField} onChange={v => setFilter("filingDateField", v)} options={[]}>
              <option value="current_status_date">Current status</option>
              <option value="filing_date">Filed</option>
              <option value="approved_date">Approved</option>
            </FilterSelect>
            <FilterSelect label="Date rule" value={filters.filingDateRule} onChange={v => setFilter("filingDateRule", v)} options={[]}>
              <option value="all">Any date</option>
              <option value="after">After date</option>
              <option value="before">Before date</option>
            </FilterSelect>
            <FilterDate label="Date" value={filters.filingDate} onChange={v => setFilter("filingDate", v)} />
            <button type="button" onClick={() => resetFilters("filingDateField", "filingDateRule", "filingDate", "filingStatus", "filingType")} className="self-end px-3 py-2 border border-zinc-200 rounded-sm bg-white text-sm font-semibold hover:bg-zinc-50">
              Reset filings
            </button>
          </FilterPanel>
          {filteredFilings.length === 0 ? (
            <div className="border border-zinc-200 bg-white p-10 rounded-sm text-center text-sm text-zinc-500">
              <FileText size={20} className="mx-auto mb-2 text-zinc-300" />
              <p>No plan filings match these filters.</p>
              <p className="text-xs mt-1 text-zinc-400">
                Filings appear once permits with job numbers are tracked and polled.
              </p>
            </div>
          ) : pagedFilings.map(f => (
            <div
              key={f.filing_number}
              onClick={() => openRecordDetails("filing", f)}
              className={`border rounded-sm bg-white overflow-hidden cursor-pointer
              ${f.filing_status === "Plan Disapproved" ? "border-red-300" :
                f.filing_status === "Plan Approved" ? "border-green-300" : "border-zinc-200"}`}
            >
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-zinc-700">{f.filing_number}</span>
                    <StatusBadge status={f.filing_status} />
                    {f.special_inspections && (
                      <span className="status-pill status-warning">Special Inspections Required</span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">{f.filing_type}</p>
                </div>
                <a href={filingDobUrl(f)}
                  target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50 shrink-0">
                  <ExternalLink size={12} /> DOB
                </a>
              </div>

              <FilingStatusSummary filing={f} compact />

              <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                <MetaChip icon={Info} label="Review type" value={f.review_type} />
                <MetaChip icon={User} label="Examiner" value={f.plan_examiner} />
                <MetaChip icon={User} label="Applicant" value={(f.applicant || "").trim() || null} />
                <MetaChip icon={User} label="Owner" value={f.owner} />
                <MetaChip icon={DollarSign} label="Est. Cost" value={f.estimated_cost ? `$${Number(f.estimated_cost).toLocaleString()}` : null} />
              </div>

              {f.filing_status === "Plan Disapproved" && f.disapproval_reason && (
                <div className="mx-4 mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  <span className="font-semibold">Disapproval reason:</span> {f.disapproval_reason}
                </div>
              )}

              {f.work_description && (
                <div className="px-4 pb-3">
                  <p className="text-xs text-zinc-500"><span className="font-medium text-zinc-700">Work description:</span> {f.work_description}</p>
                </div>
              )}
              <div className="px-4 pb-3">
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); openRecordDetails("filing", f); }}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50"
                >
                  <Info size={12} /> Full details
                </button>
              </div>
            </div>
          ))}
          <PaginationControls page={pages.filings} total={filteredFilings.length} onPage={page => setPage("filings", page)} />
        </div>
      )}

      {/* ── Violations tab ── */}
      {tab === "violations" && (
        <div className="space-y-4">
          <FilterPanel>
            <FilterSelect label="Status" value={filters.violationState} onChange={v => setFilter("violationState", v)} options={[]}>
              <option value="active">Active/open</option>
              <option value="history">Resolved/history</option>
              <option value="all">All records</option>
            </FilterSelect>
            <FilterSelect label="Source" value={filters.violationSource} onChange={v => setFilter("violationSource", v)} options={[]}>
              <option value="all">All</option>
              <option value="dob">DOB</option>
              <option value="ecb">ECB</option>
              <option value="oath">OATH</option>
              <option value="hpd">HPD</option>
              <option value="311">311</option>
            </FilterSelect>
            <FilterSelect label="Type/status" value={filters.violationType} onChange={v => setFilter("violationType", v)} options={violationTypes} />
            <FilterSelect label="Issue date" value={filters.violationDateRule} onChange={v => setFilter("violationDateRule", v)} options={[]}>
              <option value="all">Any date</option>
              <option value="after">After date</option>
              <option value="before">Before date</option>
            </FilterSelect>
            <FilterDate label="Date" value={filters.violationDate} onChange={v => setFilter("violationDate", v)} />
            <FilterCheck label="Stop-work only" checked={filters.stopWorkOnly} onChange={v => setFilter("stopWorkOnly", v)} />
            <FilterCheck label="Balance due" checked={filters.balanceDueOnly} onChange={v => setFilter("balanceDueOnly", v)} />
            <FilterCheck label="Show pre-existing" checked={filters.showPreExisting} onChange={v => setFilter("showPreExisting", v)} />
            <button type="button" onClick={() => resetFilters("violationDateRule", "violationDate", "violationState", "violationSource", "violationType", "stopWorkOnly", "balanceDueOnly", "showPreExisting")} className="self-end px-3 py-2 border border-zinc-200 rounded-sm bg-white text-sm font-semibold hover:bg-zinc-50">
              Reset violations
            </button>
          </FilterPanel>
          {filters.violationState === "active" && historyViolationCount > 0 && (
            <p className="text-xs text-zinc-500">
              {historyViolationCount} resolved or closed record{historyViolationCount === 1 ? "" : "s"} hidden. Change Status to Resolved/history to review them.
            </p>
          )}
          {filteredViolations.length === 0 ? (
            <div className="border border-zinc-200 bg-white p-10 rounded-sm text-center text-sm text-zinc-500">
              <ShieldAlert size={20} className="mx-auto mb-2 text-zinc-300" />
              <p>No violations match these filters.</p>
              <p className="text-xs mt-1 text-zinc-400">Violations are checked automatically 4× daily.</p>
            </div>
          ) : (
            <>
              <PaginationControls page={pages.violations} total={filteredViolations.length} onPage={page => setPage("violations", page)} />
              {visibleStopWorkOrder && (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-300 rounded-sm">
                  <ShieldAlert size={14} className="text-red-600" />
                  <p className="text-xs font-semibold text-red-700">This site has an active Stop-Work Order.</p>
                </div>
              )}
              {dob_violations.length > 0 && (
                <section>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                    DOB Violations ({dob_violations.length})
                  </p>
                  <div className="space-y-2">
                    {dob_violations.map(v => <ViolationCard key={v.violation_id} v={v} onOpenDetails={record => openRecordDetails("violation", record)} />)}
                  </div>
                </section>
              )}
              {ecb_violations.length > 0 && (
                <section>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 mt-4">
                    ECB Violations ({ecb_violations.length})
                  </p>
                  <div className="space-y-2">
                    {ecb_violations.map(v => <ViolationCard key={v.violation_id} v={v} onOpenDetails={record => openRecordDetails("violation", record)} />)}
                  </div>
                </section>
              )}
              {oath_violations.length > 0 && (
                <section>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 mt-4">
                    OATH Cases ({oath_violations.length})
                  </p>
                  <div className="space-y-2">
                    {oath_violations.map(v => <ViolationCard key={v.violation_id} v={v} onOpenDetails={record => openRecordDetails("violation", record)} />)}
                  </div>
                </section>
              )}
              {fdny_violations.length > 0 && (
                <section>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 mt-4">
                    FDNY Violations ({fdny_violations.length})
                  </p>
                  <div className="space-y-2">
                    {fdny_violations.map(v => <ViolationCard key={v.violation_id} v={v} onOpenDetails={record => openRecordDetails("violation", record)} />)}
                  </div>
                </section>
              )}
              {hpd_violations.length > 0 && (
                <section>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 mt-4">
                    HPD Formal Violations ({hpd_violations.length})
                  </p>
                  <div className="space-y-2">
                    {hpd_violations.map(v => <ViolationCard key={v.violation_id} v={v} onOpenDetails={record => openRecordDetails("violation", record)} />)}
                  </div>
                </section>
              )}
              {service_requests_311.length > 0 && (
                <section>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 mt-4">
                    311 Complaints ({service_requests_311.length})
                  </p>
                  <div className="space-y-2">
                    {service_requests_311.map(v => <ViolationCard key={v.violation_id} v={v} onOpenDetails={record => openRecordDetails("violation", record)} />)}
                  </div>
                </section>
              )}
              <PaginationControls page={pages.violations} total={filteredViolations.length} onPage={page => setPage("violations", page)} />
            </>
          )}
        </div>
      )}

      {/* ── Inspections tab ── */}
      {tab === "property" && (
        <PropertyTab
          data={propertyIntel}
          loading={propertyLoading}
          onRefresh={loadProperty}
        />
      )}

      {tab === "contacts" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-zinc-500">
              Public contact data from NYC Open Data. Emails are shown only if a source publishes them.
            </p>
            <button type="button" onClick={loadContacts} disabled={contactsLoading}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 text-zinc-700 disabled:opacity-50">
              <RefreshCw size={12} className={contactsLoading ? "animate-spin" : ""} />
              Refresh contacts
            </button>
          </div>

          {contactsLoading && !contactIntel ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-sm" />)}
            </div>
          ) : (
            <>
              {contactIntel?.email_status?.note && (
                <div className="border border-amber-200 bg-amber-50 px-4 py-3 rounded-sm flex items-start gap-2">
                  <Mail size={15} className="text-amber-700 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">{contactIntel.email_status.note}</p>
                </div>
              )}

              {!contactIntel?.contacts?.length ? (
                <div className="border border-zinc-200 bg-white p-10 rounded-sm text-center text-sm text-zinc-500">
                  <User size={20} className="mx-auto mb-2 text-zinc-300" />
                  <p>No public contact records were found for this job.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {pagedContacts.map((contact, idx) => (
                    <ContactCard key={`${contact.role}-${contact.name || contact.business_name || idx}-${idx}`} contact={contact} />
                  ))}
                </div>
              )}
              <PaginationControls page={pages.contacts} total={contactRows.length} onPage={page => setPage("contacts", page)} />

              {contactIntel?.source_notes?.length > 0 && (
                <div className="border border-zinc-200 bg-zinc-50 px-4 py-3 rounded-sm">
                  <p className="label-eyebrow mb-2">Source Notes</p>
                  <div className="space-y-1">
                    {contactIntel.source_notes.map(note => (
                      <p key={note} className="text-xs text-zinc-600">{note}</p>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "inspections" && (
        <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200"><tr>
              <Th>Type</Th><Th>Scheduled</Th><Th>Permit</Th><Th>Status</Th>
            </tr></thead>
            <tbody>
              {inspections.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-500">No inspections scheduled.</td></tr>
              ) : pagedInspections.map(i => (
                <tr key={i.inspection_id} className="row-hover border-b border-zinc-100">
                  <Td><Calendar size={12} className="inline mr-1 text-zinc-400" />{i.inspection_type}</Td>
                  <Td>{fmtDateTime(i.scheduled_at)}</Td>
                  <Td className="font-mono text-xs">{permits.find(p => p.permit_id === i.permit_id)?.permit_number || "—"}</Td>
                  <Td><StatusBadge status={i.status} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls page={pages.inspections} total={inspections.length} onPage={page => setPage("inspections", page)} />
        </div>
      )}

      {/* ── Activity tab ── */}
      {tab === "activity" && (
        <div className="border border-zinc-200 bg-white rounded-sm divide-y divide-zinc-100">
          {activity.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">No activity yet.</div>
          ) : pagedActivity.map(a => (
            <div key={a.log_id} className="flex items-start gap-3 px-4 py-3" data-testid={`activity-${a.log_id}`}>
              <ActivityIcon event={a.event} />
              <div className="flex-1">
                <div className="text-sm">{a.message}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{fmtDateTime(a.timestamp)}</div>
              </div>
            </div>
          ))}
          <PaginationControls page={pages.activity} total={activity.length} onPage={page => setPage("activity", page)} />
        </div>
      )}

      <RecordDetailModal detail={detailRecord} onClose={() => setDetailRecord(null)} />
    </div>
  );
}

function ActivityIcon({ event }) {
  const cls = "mt-0.5 shrink-0";
  if (event === "stop_work_order") return <ShieldAlert size={14} className={`${cls} text-red-500`} />;
  if (["violation_issued", "ecb_violation", "oath_violation", "fdny_violation", "hpd_violation", "hpd_complaint", "service_request"].includes(event)) {
    return <AlertTriangle size={14} className={`${cls} text-amber-500`} />;
  }
  if (event === "filing_status_change") return <FileText size={14} className={`${cls} text-blue-500`} />;
  return <Activity size={14} className={`${cls} text-zinc-400`} />;
}

function PaginationControls({ page, total, onPage }) {
  const totalPages = pageCount(Array.from({ length: total || 0 }));
  if ((total || 0) <= PAGE_SIZE) return null;
  const start = ((Math.max(1, page) - 1) * PAGE_SIZE) + 1;
  const end = Math.min(total, Math.max(1, page) * PAGE_SIZE);
  return (
    <div className="flex items-center justify-between gap-3 text-xs text-zinc-500 py-3">
      <span>Showing {start}-{end} of {total}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          className="px-2 py-1 border border-zinc-200 rounded-sm bg-white hover:bg-zinc-50 disabled:opacity-40"
        >
          Previous
        </button>
        <span className="px-2">Page {page} of {totalPages}</span>
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          className="px-2 py-1 border border-zinc-200 rounded-sm bg-white hover:bg-zinc-50 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function fieldLabel(key) {
  return String(key || "")
    .replace(/__/g, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, ch => ch.toUpperCase());
}

function detailValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value) || typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function nonEmptyEntries(record) {
  return Object.entries(record || {})
    .filter(([key, value]) => key !== "_id" && key !== "user_id" && detailValue(value))
    .sort(([a], [b]) => a.localeCompare(b));
}

function recordTitle(type, record) {
  if (type === "permit") return `Permit ${record.permit_number || record.job_number || ""}`.trim();
  if (type === "filing") return `Filing ${record.filing_number || record.job_number || ""}`.trim();
  return `${violationSourceLabel(record)} ${record.violation_number || record.ecb_number || record.service_request_number || "Record"}`;
}

function recordExternalUrl(type, record) {
  if (type === "permit") return permitDobUrl(record);
  if (type === "filing") return filingDobUrl(record);
  if (type === "violation") return violationDobUrl(record);
  return null;
}

function primaryDetailFields(type, record) {
  if (type === "permit") {
    return [
      ["Status", record.status],
      ["Permit type", record.permit_type],
      ["Work type", record.work_type],
      ["Issued", fmtDate(record.issuance_date)],
      ["Expires", fmtDate(record.expiration_date)],
      ["Filed", fmtDate(record.filing_date)],
      ["Owner", record.owner],
      ["Applicant", record.applicant],
      ["Contractor", record.contractor],
      ["BIN", record.bin],
      ["BBL", record.bbl],
      ["Block/Lot", record.block && record.lot ? `${record.block}/${record.lot}` : null],
      ["Estimated cost", record.estimated_cost ? `$${Number(record.estimated_cost).toLocaleString()}` : null],
    ];
  }
  if (type === "filing") {
    return [
      ["Status", record.filing_status],
      ["Raw status", record.raw_filing_status],
      ["Type", record.filing_type],
      ["Review type", record.review_type],
      ["Current status date", fmtDate(record.current_status_date || record.updated_at)],
      ["Filed", fmtDate(record.filing_date)],
      ["Approved", fmtDate(record.approved_date || record.plan_approval_date)],
      ["First permit", fmtDate(record.first_permit_date)],
      ["Applicant", record.applicant],
      ["Owner", record.owner],
      ["Plan examiner", record.plan_examiner],
      ["Estimated cost", record.estimated_cost ? `$${Number(record.estimated_cost).toLocaleString()}` : null],
      ["BIN", record.bin],
      ["BBL", record.bbl],
    ];
  }
  return [
    ["Source", violationSourceLabel(record)],
    ["Status", record.status || record.ecb_status || record.hearing_status || record.compliance_status],
    ["Type", record.violation_type || record.category],
    ["Issued", fmtDate(record.issue_date)],
    ["Served", fmtDate(record.served_date)],
    ["Hearing date", fmtDate(record.hearing_date)],
    ["Hearing status", record.hearing_status],
    ["Hearing result", record.hearing_result],
    ["Compliance", record.compliance_status || record.certification_status],
    ["Violation status", record.violation_status],
    ["Current status date", fmtDate(record.current_status_date)],
    ["Disposition", record.disposition],
    ["Resolved", fmtDate(record.disposition_date)],
    ["Severity", record.severity],
    ["HPD class", record.hpd_class],
    ["Inspection date", fmtDate(record.inspection_date)],
    ["Approved date", fmtDate(record.approved_date)],
    ["Correct by", fmtDate(record.new_correct_by_date || record.original_correct_by_date)],
    ["Certify by", fmtDate(record.new_certify_by_date || record.original_certify_by_date)],
    ["Certified", fmtDate(record.certified_date)],
    ["Apartment", record.apartment],
    ["Story", record.story],
    ["Rent impairing", record.rent_impairing],
    ["Penalty", record.penalty ? `$${Number(record.penalty).toLocaleString()}` : null],
    ["Amount paid", record.amount_paid ? `$${Number(record.amount_paid).toLocaleString()}` : null],
    ["Balance due", record.balance_due ? `$${Number(record.balance_due).toLocaleString()}` : null],
    ["Respondent", record.respondent],
    ["HPD registration", record.hpd_registration_id],
    ["HPD building", record.hpd_building_id],
    ["NOV ID", record.nov_id],
    ["Order #", record.order_number],
    ["BIN", record.bin],
    ["BBL", record.bbl],
  ];
}

function longDetailBlocks(type, record) {
  const fields = type === "permit"
    ? [["Work description", record.work_description || record.description]]
    : type === "filing"
      ? [
        ["Work description", record.work_description],
        ["Disapproval reason", record.disapproval_reason],
        ["Special inspection requirements", record.special_inspection_requirements],
        ["Progress inspection requirements", record.progress_inspection_requirements],
      ]
      : [
        ["Description", record.description || record.violation_description || record.resolution_description],
        ["Resolution", record.resolution_description],
        ["Disposition comments", record.disposition_comments],
        ["Violation details", record.violation_details],
      ];
  return fields.filter(([, value]) => detailValue(value));
}

function RecordDetailModal({ detail, onClose }) {
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  if (!detail) return null;
  const { type, record } = detail;
  const title = recordTitle(type, record);
  const externalUrl = recordExternalUrl(type, record);
  const primaryFields = primaryDetailFields(type, record).filter(([, value]) => detailValue(value));
  const longBlocks = longDetailBlocks(type, record);
  const rawFields = nonEmptyEntries(record);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/35 px-0 sm:px-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
    >
      <div
        className="w-full sm:max-w-4xl max-h-[92vh] bg-white border border-zinc-200 rounded-t-sm sm:rounded-sm shadow-xl overflow-hidden"
        onMouseDown={event => event.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-zinc-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="label-eyebrow">{fieldLabel(type)} Details</p>
            <h2 className="text-lg font-semibold text-zinc-950 mt-1 break-words">{title}</h2>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              {type === "permit" && record.status && <StatusBadge status={record.status} />}
              {type === "filing" && record.filing_status && <StatusBadge status={record.filing_status} />}
              {type === "violation" && record.is_stop_work_order && <span className="status-pill status-danger">Stop Work Order</span>}
              {type === "violation" && record.violation_state && <span className="status-pill status-neutral">{record.violation_state}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {externalUrl && (
              <a href={externalUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50">
                <ExternalLink size={12} /> Source
              </a>
            )}
            <button type="button" onClick={onClose} className="p-2 border border-zinc-200 rounded-sm hover:bg-zinc-50" aria-label="Close details">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(92vh-88px)] p-4 space-y-4">
          {longBlocks.length > 0 && (
            <section className="space-y-3">
              {longBlocks.map(([label, value]) => (
                <div key={label} className="border border-zinc-200 bg-zinc-50 rounded-sm p-3">
                  <p className="label-eyebrow mb-2">{label}</p>
                  <p className="text-sm text-zinc-800 whitespace-pre-wrap break-words leading-relaxed">{detailValue(value)}</p>
                </div>
              ))}
            </section>
          )}

          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {primaryFields.map(([label, value]) => (
              <div key={label} className="border border-zinc-200 rounded-sm px-3 py-2 min-h-16">
                <p className="label-eyebrow">{label}</p>
                <p className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap break-words">{detailValue(value)}</p>
              </div>
            ))}
          </section>

          <section className="border border-zinc-200 rounded-sm overflow-hidden">
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200">
              <p className="label-eyebrow">All Available Fields</p>
            </div>
            <div className="divide-y divide-zinc-100">
              {rawFields.map(([key, value]) => (
                <div key={key} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 px-3 py-2">
                  <p className="text-xs font-semibold text-zinc-500 break-words">{fieldLabel(key)}</p>
                  <p className="text-xs text-zinc-800 whitespace-pre-wrap break-words">{detailValue(value)}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function PropertyTab({ data, loading, onRefresh }) {
  const property = data?.property;
  const registrations = data?.hpd_registrations || [];
  const hpdContacts = data?.hpd_contacts || [];
  const managementContacts = hpdContacts.filter(c =>
    /manag|agent|responsible/i.test(`${c.type || ""} ${c.description || ""}`)
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-zinc-500">
          Property data from PLUTO, DOF assessment, and HPD registration datasets.
        </p>
        <button type="button" onClick={onRefresh} disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 text-zinc-700 disabled:opacity-50">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh property
        </button>
      </div>

      {loading && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Skeleton className="h-48 rounded-sm" />
          <Skeleton className="h-48 rounded-sm" />
        </div>
      ) : (
        <>
          <PropertySummary property={property} />

          {managementContacts.length > 0 && (
            <section className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100">
                <p className="text-sm font-semibold text-zinc-900">Property Management / Agent</p>
                <p className="text-xs text-zinc-500 mt-0.5">From HPD Registration Contacts</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
                {managementContacts.slice(0, 10).map((contact, idx) => (
                  <div key={`${contact.registration_contact_id || idx}`} className="border border-zinc-200 rounded-sm p-3">
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{contact.description || contact.type || "Registration contact"}</p>
                    <p className="text-sm font-semibold text-zinc-900 mt-1">{contact.company || contact.name || "Unnamed"}</p>
                    {contact.name && contact.company && <p className="text-xs text-zinc-500 mt-0.5">{contact.name}</p>}
                    {contact.title && <p className="text-xs text-zinc-500 mt-0.5">{contact.title}</p>}
                    {contact.business_address && <p className="text-xs text-zinc-600 mt-2 break-words">{contact.business_address}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {registrations.length > 0 && (
            <section className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
              <div className="px-4 py-3 border-b border-zinc-100">
                <p className="text-sm font-semibold text-zinc-900">HPD Registrations</p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr><Th>Registration</Th><Th>Building</Th><Th>Address</Th><Th>Last Registered</Th><Th>Ends</Th></tr>
                </thead>
                <tbody>
                  {registrations.slice(0, 10).map(reg => (
                    <tr key={reg.registrationid} className="border-b border-zinc-100">
                      <Td className="font-mono text-xs">{reg.registrationid}</Td>
                      <Td className="font-mono text-xs">{reg.buildingid || "—"}</Td>
                      <Td>{[reg.housenumber, reg.streetname, reg.zip].filter(Boolean).join(" ")}</Td>
                      <Td>{fmtDate(reg.lastregistrationdate)}</Td>
                      <Td>{fmtDate(reg.registrationenddate)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {data?.source_notes?.length > 0 && (
            <div className="border border-zinc-200 bg-zinc-50 px-4 py-3 rounded-sm">
              <p className="label-eyebrow mb-2">Source Notes</p>
              <div className="space-y-1">
                {data.source_notes.map(note => <p key={note} className="text-xs text-zinc-600">{note}</p>)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PropertySummary({ property }) {
  if (!property) {
    return (
      <div className="border border-zinc-200 bg-white p-6 rounded-sm text-sm text-zinc-500">
        Property data has not been loaded yet.
      </div>
    );
  }
  const facts = [
    ["Owner", property.owner_name],
    ["Management company", property.property_management_company],
    ["Manager", property.property_manager_name],
    ["BBL", property.bbl],
    ["BIN", property.bin],
    ["Block/Lot", property.block && property.lot ? `${property.block}/${property.lot}` : null],
    ["Building class", property.building_class],
    ["Land use", property.land_use],
    ["Year built", property.year_built],
    ["Stories", property.stories || property.num_floors],
    ["Units", property.units_total],
    ["Sq footage", property.gross_sqft || property.building_area],
    ["Lot area", property.lot_area],
    ["Zoning", property.zoning],
    ["Tax class", property.tax_class],
    ["Community board", property.community_board],
    ["HPD registration", property.hpd_registration_id],
    ["Market total", property.market_total ? `$${Number(property.market_total).toLocaleString()}` : null],
    ["Assessed total", property.assessed_total ? `$${Number(property.assessed_total).toLocaleString()}` : null],
  ].filter(([, value]) => value !== undefined && value !== null && value !== "");

  return (
    <section className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-start gap-3">
        <Building2 size={16} className="text-zinc-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-zinc-900">{property.address || "Property"}</p>
          <p className="text-xs text-zinc-500">
            {[property.borough, property.zip ? `NY ${property.zip}` : null].filter(Boolean).join(", ")}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-100">
        {facts.map(([label, value]) => (
          <div key={label} className="bg-white px-4 py-3 min-h-16">
            <p className="label-eyebrow">{label}</p>
            <p className="text-sm text-zinc-800 mt-1 break-words">{value}</p>
          </div>
        ))}
      </div>
      {property.sources?.length > 0 && (
        <div className="px-4 py-2 border-t border-zinc-100 text-xs text-zinc-500">
          Sources: {property.sources.join(", ")}
        </div>
      )}
    </section>
  );
}

function ContactCard({ contact }) {
  const title = contact.business_name || contact.name || "Unnamed contact";
  const subtitle = contact.business_name && contact.name ? contact.name : null;
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="status-pill">{contact.role}</span>
            {contact.confidence === "high" && (
              <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
                <BadgeCheck size={12} /> Official match
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-zinc-900 mt-2 break-words">{title}</p>
          {subtitle && <p className="text-xs text-zinc-500 mt-0.5 break-words">{subtitle}</p>}
        </div>
        <Briefcase size={16} className="text-zinc-300 shrink-0" />
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <ContactFact icon={Phone} label="Phone" value={contact.phone || "Not published"} muted={!contact.phone} />
        <ContactFact icon={Mail} label="Email" value={contact.email || "Not published"} muted={!contact.email} />
        <ContactFact icon={MapPin} label="Mailing address" value={contact.mailing_address || "Not published"} muted={!contact.mailing_address} wide />
        <ContactFact icon={BadgeCheck} label="License" value={[contact.license_type, contact.license].filter(Boolean).join(" ") || null} muted={!contact.license && !contact.license_type} />
      </div>

      <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between gap-3 text-xs text-zinc-500">
        <span className="break-words">Source: {contact.source}</span>
        {contact.source_record && <span className="font-mono shrink-0">{contact.source_record}</span>}
      </div>
      {contact.notes && <p className="text-[11px] text-zinc-400 mt-2">{contact.notes}</p>}
    </div>
  );
}

function ContactFact({ icon: Icon, label, value, muted = false, wide = false }) {
  return (
    <div className={`${wide ? "sm:col-span-2" : ""} flex items-start gap-2 text-xs`}>
      <Icon size={13} className={`${muted ? "text-zinc-300" : "text-zinc-500"} mt-0.5 shrink-0`} />
      <div className="min-w-0">
        <p className="text-zinc-400">{label}</p>
        <p className={`${muted ? "text-zinc-400" : "text-zinc-700"} break-words`}>{value || "Not published"}</p>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="px-4 sm:px-8 py-8 max-w-7xl mx-auto">
      <Skeleton className="h-4 w-14 mb-3" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <Skeleton className="h-3 w-32 mb-2" /><Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-4 w-48 mb-1" /><Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-9 w-28" />
      </div>
      <div className="flex gap-1 border-b border-zinc-200 mb-4">
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 w-24 mr-1" />)}
      </div>
      <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
        <div className="bg-zinc-50 border-b border-zinc-200 px-4 py-2.5 flex gap-8">
          {[...Array(7)].map((_, i) => <Skeleton key={i} className="h-3 w-16" />)}
        </div>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex gap-8 px-4 py-3 border-b border-zinc-100">
            <Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-16" />
            <Skeleton className="h-5 w-24 rounded-full" /><Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" /><Skeleton className="h-4 w-8" />
            <Skeleton className="h-6 w-20 ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}
